import React, { useEffect, useState } from "react";
import { Mic2, Sparkles, Loader2, Save } from "lucide-react";
import { getBrandVoice, saveBrandVoice } from "../../services/collavueStore";
import { trainBrandVoice } from "../../services/collavueApi";
import { useToast } from "../../contexts/ToastProvider";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/collavue/PageHeader";

const TONES = [
  "professional",
  "casual",
  "founder-led",
  "technical",
  "witty",
  "direct",
];

export default function BrandVoicePage() {
  const { user } = useAuth();
  const { push } = useToast();
  const [tone, setTone] = useState("professional");
  const [useWords, setUseWords] = useState("");
  const [avoidWords, setAvoidWords] = useState("");
  const [bannedPhrases, setBannedPhrases] = useState("");
  const [complianceRules, setComplianceRules] = useState("");
  const [examples, setExamples] = useState("");
  const [profile, setProfile] = useState(null);

  const [training, setTraining] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const v = await getBrandVoice();
      if (v) {
        setTone(v.tone || "professional");
        setUseWords((v.useWords || []).join(", "));
        setAvoidWords((v.avoidWords || []).join(", "));
        setBannedPhrases((v.bannedPhrases || []).join(", "));
        setComplianceRules(v.complianceRules || "");
        setProfile({
          tone_summary: v.tone_summary,
          writing_patterns: v.writing_patterns,
          preferred_cta_style: v.preferred_cta_style,
          response_examples: v.response_examples,
        });
      }
    })();
  }, [user]);

  const splitCsv = (s) =>
    s
      .split(/[,\n]/)
      .map((x) => x.trim())
      .filter(Boolean);

  const onSave = async () => {
    setSaving(true);
    try {
      await saveBrandVoice({
        tone,
        useWords: splitCsv(useWords),
        avoidWords: splitCsv(avoidWords),
        bannedPhrases: splitCsv(bannedPhrases),
        complianceRules,
      });
      push({ title: "Saved", variant: "success" });
    } catch (e) {
      push({ title: "Save failed", desc: e.message, variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const onTrain = async () => {
    setTraining(true);
    try {
      const r = await trainBrandVoice({
        tone,
        useWords: splitCsv(useWords),
        avoidWords: splitCsv(avoidWords),
        bannedPhrases: splitCsv(bannedPhrases),
        complianceRules,
        examples: examples
          .split(/\n-{3,}\n|\n{2,}/)
          .map((x) => x.trim())
          .filter(Boolean),
      });
      setProfile(r.profile);
      push({ title: "Brand voice trained", variant: "success" });
    } catch (e) {
      push({ title: "Training failed", desc: e.message, variant: "error" });
    } finally {
      setTraining(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <PageHeader
        title="Brand voice"
        subtitle="Train Collavue to draft replies that sound like you."
        actions={
          <button
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> Save settings
          </button>
        }
      />

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-semibold flex items-center gap-2 mb-3">
            <Mic2 className="w-4 h-4 text-indigo-600" /> Voice settings
          </h2>

          <Label>Tone</Label>
          <div className="flex flex-wrap gap-2">
            {TONES.map((t) => (
              <button
                key={t}
                onClick={() => setTone(t)}
                className={`px-3 py-1.5 rounded-full border text-sm capitalize ${
                  tone === t
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "border-slate-300 hover:bg-slate-50"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <Field
            label="Words to use"
            value={useWords}
            onChange={setUseWords}
            placeholder="practical, clear, candid"
          />
          <Field
            label="Words to avoid"
            value={avoidWords}
            onChange={setAvoidWords}
            placeholder="best-ever, guaranteed, instantly"
          />
          <Field
            label="Banned phrases"
            value={bannedPhrases}
            onChange={setBannedPhrases}
            placeholder="game-changer, revolutionary"
          />
          <Field
            label="Compliance rules"
            value={complianceRules}
            onChange={setComplianceRules}
            textarea
            placeholder="Never promise outcomes. Don't claim 10x results. Always disclose if employee."
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-semibold flex items-center gap-2 mb-3">
            Train from examples
          </h2>
          <p className="text-sm text-slate-600 mb-3">
            Paste a few example replies / posts / emails. Separate with a blank
            line or <code>---</code>.
          </p>
          <textarea
            value={examples}
            onChange={(e) => setExamples(e.target.value)}
            rows={10}
            placeholder={"Hey, we ran into the same issue last year — what worked for us was…\n---\nQuick tip: don't start with a feature list. Start with…"}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
          <button
            onClick={onTrain}
            disabled={training}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
          >
            {training ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            Train voice (3 credits)
          </button>

          {profile && (
            <div className="mt-5 border-t border-slate-100 pt-4">
              <h3 className="font-semibold mb-2">Voice profile</h3>
              {profile.tone_summary && (
                <div className="text-sm text-slate-700 mb-2">
                  <strong>Summary: </strong>
                  {profile.tone_summary}
                </div>
              )}
              {profile.preferred_cta_style && (
                <div className="text-sm text-slate-700 mb-2">
                  <strong>CTA style: </strong>
                  {profile.preferred_cta_style}
                </div>
              )}
              {(profile.writing_patterns || []).length > 0 && (
                <div className="mt-2">
                  <div className="text-xs uppercase font-semibold text-slate-500 mb-1">
                    Patterns
                  </div>
                  <ul className="text-sm text-slate-700 list-disc pl-5 space-y-0.5">
                    {profile.writing_patterns.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(profile.response_examples || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase font-semibold text-slate-500 mb-1">
                    Example replies
                  </div>
                  <div className="space-y-2">
                    {profile.response_examples.map((p, i) => (
                      <div
                        key={i}
                        className="text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 whitespace-pre-wrap"
                      >
                        {p}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5 mt-4">
      {children}
    </label>
  );
}

function Field({ label, value, onChange, textarea, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      {textarea ? (
        <textarea
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
      )}
    </div>
  );
}
